import { Subject, Warning } from '@36node-fcp/core-sdk';
import { includes, isEmpty } from 'lodash';
import { createContext, useContext, useEffect } from 'react';

import {
  MONITOR_WARNING_COUNT,
  MONITOR_WARNING_HIDDEN_COUNT,
  MONITOR_WARNING_IGNORE_MINUTES,
  MQTT_TOPIC_NOTIFY_WARNING,
} from 'src/config';
import { WatchWarningMode } from 'src/features/warning';
import { useMqtt } from 'src/lib/react-mqtt';
import { getMqttTopic } from 'src/lib/react-mqtt/hook';
import { CaseReducerActions, useSlice } from 'src/lib/react-slice';
import { readFromLocalStorage } from 'src/lib/storage';

export type GantryMapEntity = {
  gantryDevices: string[];
  codes: string[];
};

export type MonitorEntiry = {
  codes: string[];
  gantries: string[]; // 存放批量布控 gantry id
  gantryDevices: string[]; // 存放批量布控 gantry device id
  gantryMap: Record<string, GantryMapEntity>; // 单个卡口布控
};

export type MonitorState = {
  settings: {
    mute: boolean;
    motor: MonitorEntiry;
    nonMotor: MonitorEntiry;
    watchMode: WatchWarningMode;
    showRecent: boolean;

    showDetail: boolean;
    showSetting: boolean;

    selected: Warning | null;
    hiddening: boolean;
  };
  warnings: {
    all: Warning[];
    motor: Warning[];
    nonMotor: Warning[];
    hidden: Warning[];
  };
  ui: {
    warningDrawerOpen: boolean;
  };
};

/**
 * watch warning
 * 监听预警信息的 hook
 */
const MAX_WARNING_SHOW = 5;
const MONITOR_SETTTINGS_KEY = 'monitor_settings';
const MONITOR_WARNINGS_KEY = 'monitor_warnings';

const initialSettings: MonitorState['settings'] = {
  mute: true,
  motor: { codes: [], gantries: [], gantryDevices: [], gantryMap: {} },
  nonMotor: { codes: [], gantries: [], gantryDevices: [], gantryMap: {} },
  watchMode: WatchWarningMode.ALL,
  showRecent: false,
  showDetail: true,
  showSetting: true,

  selected: null,
  hiddening: false,
};

const initialWarnings: MonitorState['warnings'] = {
  all: [],
  motor: [],
  nonMotor: [],
  hidden: [],
};

const initWarningsData = () => {
  const warnings = readFromLocalStorage(MONITOR_WARNINGS_KEY, initialWarnings) as MonitorState['warnings'];
  if (!isEmpty(warnings.hidden)) {
    warnings.hidden.reverse().forEach((w) => {
      warnings.all.unshift(w);
      if (w.subject === Subject.VEHICLE) {
        warnings.motor.unshift(w);
      } else {
        warnings.nonMotor.unshift(w);
      }
    });
    warnings.hidden = [];
  }

  return warnings;
};

const initMonitor = (): MonitorState => ({
  settings: {
    ...readFromLocalStorage(MONITOR_SETTTINGS_KEY, initialSettings),
    watchMode: WatchWarningMode.ALL,
    selected: null,
    hiddening: false,
  },
  warnings: initWarningsData(),
  ui: {
    warningDrawerOpen: false,
  },
});

function addToWarnings(warnings: Warning[], warning: Warning, max = MONITOR_WARNING_COUNT) {
  warnings.unshift(warning);
  if (warnings.length > max) {
    warnings.pop();
  }
}

function checkIgnoreWarning(warning: Warning, codes: string[]) {
  let ret = false;
  const now = Date.now();

  for (let i = 0; i < warning.illegals.length; i++) {
    const illegal = warning.illegals[i];
    const illegalTime = new Date(illegal.capAt).getTime();
    if (codes?.includes(illegal.code) && now - illegalTime <= MONITOR_WARNING_IGNORE_MINUTES * 60 * 1000) {
      ret = true;
      break;
    }
  }
  return ret;
}

function checkAndAddWarning(entiry: MonitorEntiry, warning: Warning, showRecent: boolean, ...targets: Warning[][]) {
  const pushToTargets = (w: Warning) => {
    if (targets.length === 1) {
      // 表示是隐藏列表添加
      addToWarnings(targets[0], w, MONITOR_WARNING_HIDDEN_COUNT);
    } else {
      targets.forEach((t) => addToWarnings(t, w));
    }
  };

  // 批量布控命中
  let shouldPush = false;
  if (entiry.gantryDevices?.includes(warning.gantryDevice)) {
    if (warning.blacklist?.state === 'OPEN') {
      shouldPush = true;
    } else {
      if (!showRecent) {
        if (
          entiry.codes?.includes(warning.code) ||
          entiry.codes?.some((code) => warning.illegals?.map((item) => item.code).includes(code))
        ) {
          shouldPush = true;
        }
      } else if (checkIgnoreWarning(warning, entiry.codes)) {
        shouldPush = true;
      }
    }
  }

  if (shouldPush) {
    pushToTargets(warning);
    return;
  }

  // 单个卡口布控命中
  const gantryMapEntities = entiry.gantryMap[warning.gantry.id];
  if (gantryMapEntities && gantryMapEntities.gantryDevices?.includes(warning.gantryDevice)) {
    if (warning.blacklist?.state === 'OPEN') {
      shouldPush = true;
    } else {
      if (!showRecent) {
        if (
          gantryMapEntities.codes?.includes(warning.code) ||
          gantryMapEntities.codes?.some((code) => warning.illegals?.map((item) => item.code).includes(code))
        ) {
          shouldPush = true;
        }
      } else if (checkIgnoreWarning(warning, gantryMapEntities.codes)) {
        shouldPush = true;
      }
    }
  }

  if (shouldPush) {
    pushToTargets(warning);
    return;
  }
}

function batchInput(entiry: MonitorEntiry) {
  if (Object.keys(entiry.gantryMap).length > 0) {
    Object.keys(entiry.gantryMap).forEach((key) => {
      if (includes(entiry.gantries, key)) {
        delete entiry.gantryMap[key];
      }
    });
  }
}

const monitorReducers = {
  set: (state: MonitorState, settings: Partial<MonitorState['settings']>) => {
    state.settings = { ...state.settings, ...settings };
  },
  reset: (state: MonitorState) => {
    state.settings.motor = initialSettings.motor;
    state.settings.nonMotor = initialSettings.nonMotor;
  },
  clearWarnings: (state: MonitorState) => {
    state.warnings.all = [];
    state.warnings.motor = [];
    state.warnings.nonMotor = [];
    state.warnings.hidden = [];

    state.settings.selected = null;
    state.settings.hiddening = false;
  },
  toggleWarningDrawer: (state: MonitorState) => {
    state.ui.warningDrawerOpen = !state.ui.warningDrawerOpen;
  },
  openWarningDrawer: (state: MonitorState) => {
    state.ui.warningDrawerOpen = true;
  },
  closeWarningDrawer: (state: MonitorState) => {
    state.ui.warningDrawerOpen = false;
  },
  selectWarning: (state: MonitorState, warning: Warning | null) => {
    state.settings.selected = warning;
  },
  deleteWarning: (state: MonitorState, warning: Warning | null) => {
    if (warning) {
      // 从列表中移除
      if (warning.subject === Subject.VEHICLE) {
        state.warnings.motor = state.warnings.motor.filter((w) => w.id !== warning.id);
      } else {
        state.warnings.nonMotor = state.warnings.nonMotor.filter((w) => w.id !== warning.id);
      }
      state.warnings.all = state.warnings.all.filter((w) => w.id !== warning.id);
    }
  },
  loadWarnings: ({ settings, warnings }: MonitorState) => {
    // 将hidden数组里的数据全部写入all数组
    // 将hidden数组里的数据按类型分别写入motor数组和nonMotor数组
    // 写入时，需要从后往前
    // 清空hidden数组，清空已选中，关闭隐藏
    warnings.hidden.reverse().forEach((w) => {
      addToWarnings(warnings.all, w);
      if (w.subject === Subject.VEHICLE) {
        addToWarnings(warnings.motor, w);
      } else {
        addToWarnings(warnings.nonMotor, w);
      }
    });
    warnings.hidden = [];

    settings.hiddening = false;
    if (!warnings.all.find((w) => settings.selected?.id === w.id)) {
      settings.selected = null;
    }
  },
  setBatchGantries: (
    state: MonitorState,
    payload: {
      motor: {
        codes: string[];
        gantries: string[];
        gantryDevices: string[];
      };
      nonMotor: {
        codes: string[];
        gantries: string[];
        gantryDevices: string[];
      };
    }
  ) => {
    const { motor, nonMotor } = payload;
    state.settings.motor = { ...state.settings.motor, ...motor };
    batchInput(state.settings.motor);
    state.settings.nonMotor = { ...state.settings.nonMotor, ...nonMotor };
    batchInput(state.settings.nonMotor);
  },
  setMotorGantryMapById: (
    state: MonitorState,
    payload: { gantryId: string; codes: string[]; gantryDevices: string[]; allDevices: string[] }
  ) => {
    const { gantryId, codes = [], gantryDevices = [], allDevices = [] } = payload;
    state.settings.motor.gantryMap[gantryId] = { gantryDevices, codes };
    state.settings.motor.gantries = state.settings.motor.gantries.filter((id) => id !== gantryId);
    state.settings.motor.gantryDevices = state.settings.motor.gantryDevices.filter((id) => !allDevices.includes(id));
  },
  deleteMotorGantryMapById: (
    {
      settings: {
        motor: { gantryMap },
      },
    }: MonitorState,
    gantryId: string
  ) => {
    delete gantryMap[gantryId];
  },
  setNonMotorGantryMapById: (
    state: MonitorState,
    payload: { gantryId: string; codes: string[]; gantryDevices: string[]; allDevices: string[] }
  ) => {
    const { gantryId, codes = [], gantryDevices = [], allDevices = [] } = payload;
    state.settings.nonMotor.gantryMap[gantryId] = { gantryDevices, codes };
    state.settings.nonMotor.gantries = state.settings.nonMotor.gantries.filter((id) => id !== gantryId);
    state.settings.nonMotor.gantryDevices = state.settings.nonMotor.gantryDevices.filter(
      (id) => !allDevices.includes(id)
    );
  },
  deleteNonMotorGantryMapById: (
    {
      settings: {
        nonMotor: { gantryMap },
      },
    }: MonitorState,
    gantryId: string
  ) => {
    delete gantryMap[gantryId];
  },
  add: ({ settings, warnings }: MonitorState, warning: Warning) => {
    const { motor, nonMotor, watchMode, hiddening, selected, showRecent } = settings;
    // 若开启隐藏，则数据写入hidden数组
    //若未开启隐藏，查看当前模式的数组长度，如果≥5，开启隐藏，并将数据写入hidden数组，否则继续写入当前模式的数组
    if (hiddening || (!!selected && warnings[watchMode].length >= MAX_WARNING_SHOW)) {
      // 机动车
      if (warning.subject === Subject.VEHICLE) {
        checkAndAddWarning(motor, warning, showRecent, warnings.hidden);
      }
      // 非机动车
      if (warning.subject === Subject.NONMOTOR || warning.subject === Subject.PERSON) {
        checkAndAddWarning(nonMotor, warning, showRecent, warnings.hidden);
      }

      if (!hiddening) {
        settings.hiddening = true;
      }
    } else {
      // 机动车
      if (warning.subject === Subject.VEHICLE) {
        checkAndAddWarning(motor, warning, showRecent, warnings.motor, warnings.all);
      }

      // 非机动车
      if (warning.subject === Subject.NONMOTOR || warning.subject === Subject.PERSON) {
        checkAndAddWarning(nonMotor, warning, showRecent, warnings.nonMotor, warnings.all);
      }
    }
  },
};

const MonitorSettingsContext = createContext<MonitorState['settings']>(null);
const MonitorWarningsContext = createContext<MonitorState['warnings']>(null);
const MonitorDispatchContext = createContext<CaseReducerActions<typeof monitorReducers>>(null);
const MonitorUIContext = createContext<MonitorState['ui']>(null);

export function MonitorProvider({ children }) {
  const [{ settings, warnings, ui }, dispatch] = useSlice(monitorReducers, initMonitor());
  useMqtt(getMqttTopic(MQTT_TOPIC_NOTIFY_WARNING), { onMessage: dispatch.add });
  useEffect(() => {
    localStorage.setItem(MONITOR_SETTTINGS_KEY, JSON.stringify(settings));
  }, [settings]);
  useEffect(() => {
    localStorage.setItem(MONITOR_WARNINGS_KEY, JSON.stringify(warnings));
  }, [warnings]);

  return (
    <MonitorDispatchContext.Provider value={dispatch}>
      <MonitorSettingsContext.Provider value={settings}>
        <MonitorWarningsContext.Provider value={warnings}>
          <MonitorUIContext.Provider value={ui}>{children}</MonitorUIContext.Provider>
        </MonitorWarningsContext.Provider>
      </MonitorSettingsContext.Provider>
    </MonitorDispatchContext.Provider>
  );
}

export const useMonitorSettings = () => {
  const dispatch = useContext(MonitorDispatchContext);
  const settings = useContext(MonitorSettingsContext);
  return [settings, dispatch] as const;
};

export const useMonitorWarnings = () => {
  const dispatch = useContext(MonitorDispatchContext);
  const warnings = useContext(MonitorWarningsContext);
  return [warnings, dispatch] as const;
};

export const useMonitorDispatch = () => {
  return useContext(MonitorDispatchContext);
};

export const useMonitor = () => {
  const settings = useContext(MonitorSettingsContext);
  const warnings = useContext(MonitorWarningsContext);
  const dispatch = useContext(MonitorDispatchContext);
  const ui = useContext(MonitorUIContext);
  return [{ settings, warnings, ui }, dispatch] as const;
};
